<template>
  <div class="position-relative" :style="{ minHeight }">
    <BarchartFilter
      :nameField="timelineNameFieldGrouped"
      :valueField="timelineValueField"
      :filterObject="mergeFilter({ diagnoses: null })"
      :isChecked="isSelectedDiagnoses"
      :data="zoomedDiagnoses"
      :options="timelineOptions('Diagnosis')"
      :isFiltering="isFiltering.diagnoses"
      :height="chartHeight"
      @checked-changed="chartSelected('diagnoses', $event)"
      @chart-created="loadData('diagnoses', $event)"
      @refresh-data="loadData('diagnoses', $event)"
    />
    <BarchartFilter
      :nameField="timelineNameFieldGrouped"
      :valueField="timelineValueField"
      :filterObject="mergeFilter({ drugs: null })"
      :isChecked="isSelectedDrugs"
      :data="zoomedDrugs"
      :options="timelineOptions('Drugs')"
      :isFiltering="isFiltering.drugs"
      :height="chartHeight"
      @checked-changed="chartSelected('drugs', $event)"
      @chart-created="loadData('drugs', $event)"
      @refresh-data="loadData('drugs', $event)"
    />
    <BarchartFilter
      :nameField="timelineNameFieldGrouped"
      :valueField="timelineValueField"
      :filterObject="mergeFilter({ procedures: null })"
      :isChecked="isSelectedProcedures"
      :data="zoomedProcedures"
      :options="timelineOptions('Procedures')"
      :isFiltering="isFiltering.procedures"
      :height="chartHeight"
      @checked-changed="chartSelected('procedures', $event)"
      @chart-created="loadData('procedures', $event)"
      @refresh-data="loadData('procedures', $event)"
    />
    <HvhChart :visualization="zoomTimelineData" :options="zoomTimelineOptions" @data-zoomed="dataZoomed" height="40px" />
  </div>
</template>

<script>
import axios from '../../axios';
import { Debouncer, mergeFilters, valueDisplayFormat } from '../../libraries/common';
import { stringifyLiveFilters } from '../../libraries/liveFilters';
import BarchartFilter from '../analysis/filters/BarchartFilter.vue';
import HvhChart from '../charts/HvhChart.vue';

var timelineNameField = 'timeSinceIndex';
var timelineNameFieldGrouped = `${timelineNameField}Grouped`;
var timelineValueField = 'patientPercentage';

var chartHeight = 175;
export default {
  components: { HvhChart, BarchartFilter },
  props: ['data', 'filterObject',],
  data() {
    return {
      dataZoom: {
        start: 0,
        end: 100,
      },
      debouncer: new Debouncer(2000),
      dataZoomDebouncer: new Debouncer(2),
      chartHeight: `${chartHeight}px`,
      zoomTimelineOptions: {
        series: [{ animation: false, }],
        grid: {
          bottom: '0',
          left: '30px',
          right: '20px',
          top: '0',
        },
        xAxis: {
          show: false,
        },
        yAxis: {
          show: false,
        },
        labelFormatter: value => {
          return Math.round((value + this.timelineMin) / 30);
        },
        dataZoom: [{
          type: 'inside',
          start: 0,
          end: 100
        },
        {
          start: 0,
          end: 100
        }]
      },
      cancelRequest: {},
      loadedData: {},
      timelineNameFieldGrouped,
      timelineValueField,
      isFiltering: {},
      selected: { diagnoses: {}, drugs: {}, proedures: {}, },
      zoomParameters: { timelineMin: 0, timelineMax: 0, multiplier: 1 },
    };
  },
  computed: {
    sortedData() {
      return Object.values(this.loadedData)
        .flatMap(f => f)
        .sortBy(f => f[timelineNameField]);
    },
    timelineMin() {
      return (this.sortedData.first() || {})[timelineNameField] || 0;
    },
    timelineMax() {
      return (this.sortedData.last() || {})[timelineNameField] || 0;
    },
    zoomedDiagnoses() {
      var { timelineMin, timelineMax, multiplier } = this.zoomParameters;
      return this.createChartData(this.loadedData.diagnoses || [], timelineMin, timelineMax, multiplier);
    },
    zoomedDrugs() {
      var { timelineMin, timelineMax, multiplier } = this.zoomParameters;
      return this.createChartData(this.loadedData.drugs || [], timelineMin, timelineMax, multiplier);
    },
    zoomedProcedures() {
      var { timelineMin, timelineMax, multiplier } = this.zoomParameters;
      return this.createChartData(this.loadedData.procedures || [], timelineMin, timelineMax, multiplier);
    },
    zoomTimelineData() {
      var data = this.createChartData(this.sortedData, Math.floor(this.timelineMin), Math.ceil(this.timelineMax), 1);
      return {
        data,
        name_field: timelineNameFieldGrouped,
        value_field: timelineValueField,
        visualization_type: 'barchart',
      };
    },
    minHeight() {
      return `${3 * chartHeight + 40}px`;
    },
    isFilteringAny() {
      return Object.keys(this.isFiltering).find(key => this.isFiltering[key]);
    },
  },
  methods: {
    loadData(prop, filterObject) {
      if (this.cancelRequest[prop])
        this.cancelRequest[prop]();
      this.$set(this.isFiltering, prop, true);
      axios.post('/PatientJourney/GetData', {
        component: `PatientTimeline${prop.capitalize()}`,
        ...stringifyLiveFilters(filterObject),
      }, {
        cancelToken: new axios.CancelToken(c => this.cancelRequest[prop] = c)
      }).then(({ data }) => {
        var totalPatients = data.sum(f => f.patientCount);
        var timelineData = data
          .filter(f => f.patientCount / totalPatients > 0.005)
          .map(f => ({ ...f, patientPercentage: f.patientCount / f.cohortCount * 100 }));
        this.$set(this.loadedData, prop, timelineData);
        this.$set(this.isFiltering, prop, false);
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          console.log(error);
          this.$set(this.isFiltering, prop, false);
          this.disableIfTimeout(error);
        }
      });
    },
    chartSelected(prop, event) {
      var isInCollection = (this.filterObject[prop] || []).includes(event.description);

      var filterObject = {
        ...this.filterObject,
        [prop]: (isInCollection ? this.filterObject[prop].filter(f => f !== event.description) : [...(this.filterObject[prop] || []), event.description]),
      };

      this.$emit('update:filterObject', filterObject);
    },
    isSelectedDiagnoses(item) {
      return (this.selected.diagnoses || {})[item.description];
    },
    isSelectedDrugs(item) {
      return (this.selected.drugs || {})[item.description];
    },
    isSelectedProcedures(item) {
      return (this.selected.procedures || {})[item.description];
    },
    dataZoomed(event) {
      this.dataZoomDebouncer.clear().push(() => this.dataZoom = event);
    },
    createChartData(timelineData, timelineMin, timelineMax, multiplier) {
      var grouped = timelineData
        .reduce((acc, f) => {
          var key = Math.floor(f[timelineNameField] * multiplier);
          var arr = acc[key];
          if (!arr) arr = acc[key] = [];
          arr.push({ ...f });
          return acc;
        }, {});

      Object.entries(grouped)
        .forEach(([key, items]) => {
          if (!((key / multiplier) % 30)) {
            var item = items.find(f => f[timelineNameField] === key) || items.first();
            item[timelineNameFieldGrouped] = key / multiplier / 30;
          }
        });

      var data = (timelineMin * multiplier).rangeTo(timelineMax * multiplier)
        .flatMap(timeline => grouped[timeline] || [{ [timelineValueField]: 0, ...(!((timeline / multiplier) % 30) ? { [timelineNameFieldGrouped]: (timeline / multiplier / 30) } : {}) }]);

      return data;
    },
    timelineOptions(label) {
      return {
        series: [{ animation: false, barWidth: 5 }],
        grid: {
          bottom: '20px',
          left: '60px',
          right: '20px',
          top: '10px',
          containLabel: false,
        },
        xAxis: {
          axisLabel: {
            interval: (index, value) => !isNaN(value),
          },
        },
        yAxis: {
          name: `% of Patients - ${label}`,
          nameGap: 40,
          nameTextStyle: {
            fontWeight: 'bold',
            fontSize: 13
          },
        },
        tooltip: {
          formatter: params => {
            var {
              cohortCount,
              commercialInsCount,
              description,
              femaleCount,
              medicaidInsCount,
              medicareInsCount,
              otherInsCount,
              patientCount,
              patientPercentage,
              totalAge,
            } = params.data.data;
            const { percentages, number, } = valueDisplayFormat;
            return [
              `<b>${description}</b>`,
              `% of Patients: <b>${percentages(patientPercentage / 100)}</b>`,
              `% of Females: <b>${percentages(femaleCount / cohortCount)}</b>`,
              `Median Age: <b>${number(totalAge / patientCount)}</b>`,
              `% Commercial: <b>${percentages(commercialInsCount / cohortCount)}</b>`,
              `% Other Insurance: <b>${percentages(otherInsCount / cohortCount)}</b>`,
              `% Medicare: <b>${percentages(medicareInsCount / cohortCount)}</b>`,
              `% Medicaid: <b>${percentages(medicaidInsCount / cohortCount)}</b>`,
            ].join('<br />');
          },
        },
      };
    },
    mergeFilter(obj) {
      return mergeFilters(this.filterObject, obj);
    },
    calculateZoomParameters() {
      var delta = this.timelineMax - this.timelineMin;
      var timelineMin = Math.floor(this.timelineMin + delta * this.dataZoom.start / 100);
      var timelineMax = Math.ceil(this.timelineMax - delta * (100 - this.dataZoom.end) / 100);
      var zoom = (this.dataZoom.end - this.dataZoom.start) / 100 * 2;
      var multiplier = Math.round(Math.max(Math.min(1 / zoom, 20), 1));
      this.zoomParameters = { timelineMin, timelineMax, multiplier };
    },
    disableIfTimeout(error) {
      if (error.response.status === 504) {
        this.$alert('Error', 'Loading of data failed. Please reload page to try loading data again.');
      }
    },
  },
  watch: {
    'filterObject'(value, oldValue) {
      ['diagnoses', 'drugs', 'procedures']
        .forEach(prop => {
          if ((value[prop] || []).join(',') !== (oldValue[prop] || []).join(',')) {
            this.$set(this.selected, prop, value[prop].reduce((acc, key) => { acc[key] = true; return acc; }, {}));
          }
        });
    },
    timelineMin(value, oldValue) {
      if (value !== oldValue) this.calculateZoomParameters();
    },
    timelineMax(value, oldValue) {
      if (value !== oldValue) this.calculateZoomParameters();
    },
    dataZoom(value, oldValue) {
      if (value.start !== oldValue.start || value.end !== oldValue.end) this.calculateZoomParameters();
    },
    isFilteringAny(value) {
      this.$emit('is-filtering', value);
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisPatientJourneyTimelineCharts'] = this;
  },
}
</script>
